/**
 * AdMob Service for Mobile App
 * Handles AdMob initialization, ad loading, and error handling
 */

import { AdMob, BannerAdOptions, BannerAdSize, BannerAdPosition, RewardAdOptions } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

export interface AdMobConfig {
  appId: string;
  enabled: boolean;
  adUnits: {
    banner: string;
    interstitial: string;
    rewarded: string;
  };
}

export interface AdMobError {
  code: string;
  message: string;
  details?: any;
}

class AdMobService {
  private initialized = false;
  private config: AdMobConfig;

  constructor() {
    this.config = {
      appId: process.env.VUE_APP_ADMOB_APP_ID || '',
      enabled: process.env.VUE_APP_ADMOB_ENABLED === 'true',
      adUnits: {
        banner: process.env.VUE_APP_ADMOB_BANNER_ID || '',
        interstitial: process.env.VUE_APP_ADMOB_INTERSTITIAL_ID || '',
        rewarded: process.env.VUE_APP_ADMOB_REWARDED_ID || ''
      }
    };
  }

  /**
   * Initialize AdMob service
   */
  async initialize(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('AdMob is only available on native platforms');
      return false;
    }

    if (!this.config.enabled) {
      console.warn('AdMob is disabled or not configured');
      return false;
    }

    if (!this.isConfigValid()) {
      const missing = this.getMissingConfig();
      console.error('AdMob configuration is invalid. Missing:', missing);
      return false;
    }

    try {
      await AdMob.initialize({
        testingDevices: process.env.NODE_ENV === 'development' ? ['YOUR_TEST_DEVICE_ID'] : [],
        initializeForTesting: process.env.NODE_ENV === 'development'
      });

      this.initialized = true;
      console.log('AdMob service initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize AdMob:', error);
      return false;
    }
  }

  /**
   * Show banner ad
   */
  async showBannerAd(position: BannerAdPosition = BannerAdPosition.BOTTOM_CENTER): Promise<boolean> {
    if (!this.initialized || !Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const options: BannerAdOptions = {
        adId: this.config.adUnits.banner,
        adSize: BannerAdSize.BANNER,
        position: position,
        margin: 0,
        isTesting: process.env.NODE_ENV === 'development'
      };

      await AdMob.showBanner(options);
      return true;
    } catch (error) {
      console.error('Failed to show banner ad:', error);
      return false;
    }
  }

  /**
   * Hide banner ad
   */
  async hideBannerAd(): Promise<void> {
    if (!this.initialized || !Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await AdMob.hideBanner();
    } catch (error) {
      console.error('Failed to hide banner ad:', error);
    }
  }

  /**
   * Show interstitial ad
   */
  async showInterstitialAd(): Promise<boolean> {
    if (!this.initialized || !Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const options = {
        adId: this.config.adUnits.interstitial,
        isTesting: process.env.NODE_ENV === 'development'
      };

      await AdMob.prepareInterstitial(options);
      await AdMob.showInterstitial();
      return true;
    } catch (error) {
      console.error('Failed to show interstitial ad:', error);
      return false;
    }
  }

  /**
   * Show rewarded ad
   */
  async showRewardedAd(): Promise<boolean> {
    if (!this.initialized || !Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const options: RewardAdOptions = {
        adId: this.config.adUnits.rewarded,
        isTesting: process.env.NODE_ENV === 'development'
      };

      await AdMob.prepareRewardVideoAd(options);
      const result = await AdMob.showRewardVideoAd();
      return !!result;
    } catch (error) {
      console.error('Failed to show rewarded ad:', error);
      return false;
    }
  }

  /**
   * Check if configuration is valid
   */
  private isConfigValid(): boolean {
    return !!(
      this.config.appId &&
      this.config.adUnits.banner &&
      this.config.adUnits.interstitial &&
      this.config.adUnits.rewarded
    );
  }

  /**
   * Get missing configuration keys
   */
  private getMissingConfig(): string[] {
    const missing: string[] = [];
    
    if (!this.config.appId) {
      missing.push('VUE_APP_ADMOB_APP_ID');
    }
    
    if (!this.config.adUnits.banner) {
      missing.push('VUE_APP_ADMOB_BANNER_ID');
    }
    
    if (!this.config.adUnits.interstitial) {
      missing.push('VUE_APP_ADMOB_INTERSTITIAL_ID');
    }
    
    if (!this.config.adUnits.rewarded) {
      missing.push('VUE_APP_ADMOB_REWARDED_ID');
    }
    
    return missing;
  }

  /**
   * Get configuration
   */
  getConfig(): AdMobConfig {
    return { ...this.config };
  }

  /**
   * Check if AdMob is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled && Capacitor.isNativePlatform();
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const admobService = new AdMobService();
export default admobService;